import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** How a catalog-enrichment list renders: rows or a card grid. */
export type ListView = 'list' | 'cards';
/** Row / card density for a catalog-enrichment list. */
export type ListDensity = 'compact' | 'normal' | 'spacious';

/** One list's remembered presentation. */
export interface ListViewPrefs {
  view: ListView;
  density: ListDensity;
}

interface ListViewState {
  /** Prefs per stable list key (e.g. 'run-products', 'product-master'). */
  prefs: Record<string, ListViewPrefs>;
  setPrefs: (listKey: string, patch: Partial<ListViewPrefs>) => void;
}

/**
 * Remembers each catalog-enrichment list's view (list / cards) and density per user, so a
 * reviewer's chosen layout survives navigation and reloads. Persisted to localStorage.
 */
export const useListViewStore = create<ListViewState>()(
  persist(
    (set) => ({
      prefs: {},
      setPrefs: (listKey, patch) => {
        set((s) => {
          const current = s.prefs[listKey] ?? { view: 'list', density: 'normal' };
          return { prefs: { ...s.prefs, [listKey]: { ...current, ...patch } } };
        });
      },
    }),
    { name: 'catalog-enrichment-list-view' },
  ),
);
